/**
 * Letter Performance Matrix - Widget 2
 * 
 * Shows detailed statistics for each letter with sortable table/chart views
 * Includes accuracy, speed, practice count, trends, and finger assignments
 */

import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ScatterChart, Scatter, Cell
} from 'recharts';
import { LetterPerformanceData, TimePeriod, ViewMode } from '@/types/statistics';

interface LetterPerformanceMatrixProps {
  data: LetterPerformanceData[];
  loading: boolean;
  period: TimePeriod;
}

type SortField = 'letter' | 'accuracy' | 'avgSpeed' | 'practiceCount' | 'trend';
type SortDirection = 'asc' | 'desc';

const LetterPerformanceMatrix: React.FC<LetterPerformanceMatrixProps> = ({ 
  data, 
  loading, 
  period 
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortField, setSortField] = useState<SortField>('practiceCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterOption, setFilterOption] = useState<'all' | 'problematic' | 'improving' | 'mastered'>('all');

  // Filtered and sorted data
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Apply filters
    switch (filterOption) {
      case 'problematic':
        filtered = filtered.filter(letter => 
          letter.accuracy < 90 || letter.trend === 'declining'
        );
        break;
      case 'improving':
        filtered = filtered.filter(letter => letter.trend === 'improving');
        break;
      case 'mastered':
        filtered = filtered.filter(letter => 
          letter.accuracy >= 95 && letter.avgSpeed <= 200
        );
        break;
      default:
        // Show all
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'letter':
          aValue = a.letter;
          bValue = b.letter;
          break;
        case 'accuracy':
          aValue = a.accuracy;
          bValue = b.accuracy;
          break;
        case 'avgSpeed':
          aValue = a.avgSpeed;
          bValue = b.avgSpeed;
          break;
        case 'practiceCount':
          aValue = a.practiceCount;
          bValue = b.practiceCount;
          break;
        case 'trend':
          const trendOrder = { 'improving': 3, 'stable': 2, 'declining': 1 };
          aValue = trendOrder[a.trend];
          bValue = trendOrder[b.trend];
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, sortField, sortDirection, filterOption]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (loading) {
    return <LetterPerformanceSkeletonBlock />;
  }

  if (data.length === 0) {
    return <EmptyLetterPerformanceBlock period={period} />;
  }

  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            🔤 Letter Performance
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            Detailed analysis of each letter • {data.length} letters tracked
          </p>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Filter Dropdown */}
          <select
            value={filterOption}
            onChange={(e) => setFilterOption(e.target.value as any)}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #475569', background: '#334155', color: '#e2e8f0', fontSize: '11px' }}
          >
            <option value="all">All Letters ({data.length})</option>
            <option value="problematic">
              Problematic ({data.filter(l => l.accuracy < 90 || l.trend === 'declining').length})
            </option>
            <option value="improving">
              Improving ({data.filter(l => l.trend === 'improving').length})
            </option>
            <option value="mastered">
              Mastered ({data.filter(l => l.accuracy >= 95 && l.avgSpeed <= 200).length})
            </option>
          </select>

          {/* View Mode Toggle */}
          <div style={{ display: 'flex', background: '#334155', borderRadius: '6px', padding: '2px' }}>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                background: viewMode === 'table' ? '#475569' : 'transparent',
                color: '#e2e8f0'
              }}
            >
              📋 Table
            </button>
            <button
              onClick={() => setViewMode('chart')}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                background: viewMode === 'chart' ? '#475569' : 'transparent',
                color: '#e2e8f0'
              }}
            >
              📊 Chart
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <TableView 
          data={processedData}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      ) : (
        <ChartView data={processedData} />
      )}

      {/* Summary Stats */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #475569' }}>
        <SummaryStats data={data} filteredData={processedData} />
      </div>
    </div>
  );
};

// Table View Component
const TableView: React.FC<{
  data: LetterPerformanceData[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}> = ({ data, sortField, sortDirection, onSort }) => {

  const getSortIcon = (field: SortField): string => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '⬆️' : '⬇️';
  };

  const getTrendIcon = (trend: string): string => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      default: return '📊';
    }
  };



  const getAccuracyColorStyle = (accuracy: number) => {
    if (accuracy >= 98) return { color: '#166534', background: '#dcfce7' };
    if (accuracy >= 95) return { color: '#1e40af', background: '#dbeafe' };
    if (accuracy >= 90) return { color: '#92400e', background: '#fef3c7' };
    return { color: '#dc2626', background: '#fee2e2' };
  };



  const getSpeedColorStyle = (speed: number) => {
    if (speed <= 150) return { color: '#16a34a' };
    if (speed <= 250) return { color: '#ca8a04' };
    return { color: '#dc2626' };
  };

  const getTrendColorStyle = (trend: string) => {
    if (trend === 'improving') return { background: '#166534', color: '#dcfce7' };
    if (trend === 'declining') return { background: '#dc2626', color: '#fee2e2' };
    return { background: '#475569', color: '#e2e8f0' };
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #475569' }}>
            <th 
              style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}
              onClick={() => onSort('letter')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Letter {getSortIcon('letter')}
              </div>
            </th>
            <th 
              style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}
              onClick={() => onSort('practiceCount')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Practice {getSortIcon('practiceCount')}
              </div>
            </th>
            <th 
              style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}
              onClick={() => onSort('accuracy')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Accuracy {getSortIcon('accuracy')}
              </div>
            </th>
            <th 
              style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}
              onClick={() => onSort('avgSpeed')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Avg Speed {getSortIcon('avgSpeed')}
              </div>
            </th>
            <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', fontSize: '11px' }}>
              Best Time
            </th>
            <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', fontSize: '11px' }}>
              Finger
            </th>
            <th 
              style={{ textAlign: 'left', padding: '8px 6px', fontWeight: '500', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}
              onClick={() => onSort('trend')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Trend {getSortIcon('trend')}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((letter) => (
            <tr key={letter.letter} style={{ borderBottom: '1px solid #334155' }}>
              {/* Letter */}
              <td style={{ padding: '8px 6px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', color: '#1e293b', background: '#e2e8f0', padding: '4px 6px', borderRadius: '4px' }}>
                  {letter.letter}
                </span>
              </td>

              {/* Practice Count */}
              <td style={{ padding: '8px 6px', color: '#e2e8f0', fontSize: '12px' }}>
                {letter.practiceCount.toLocaleString()}
              </td>

              {/* Accuracy */}
              <td style={{ padding: '8px 6px' }}>
                <span style={{ 
                  fontWeight: '500', 
                  padding: '2px 6px', 
                  borderRadius: '4px', 
                  fontSize: '11px',
                  ...getAccuracyColorStyle(letter.accuracy)
                }}>
                  {letter.accuracy}%
                </span>
                {letter.recentAccuracy !== letter.accuracy && (
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                    Recent: {letter.recentAccuracy}%
                  </div>
                )}
              </td>

              {/* Average Speed */}
              <td style={{ padding: '8px 6px' }}>
                <span style={{ fontWeight: '500', fontSize: '12px', ...getSpeedColorStyle(letter.avgSpeed) }}>
                  {letter.avgSpeed}ms
                </span>
              </td>

              {/* Best Time */}
              <td style={{ padding: '8px 6px' }}>
                <span style={{ fontWeight: '600', color: '#3b82f6', fontSize: '12px' }}>
                  {letter.bestTime > 0 ? `${letter.bestTime}ms` : '--'}
                </span>
              </td>

              {/* Finger Assignment */}
              <td style={{ padding: '8px 6px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', background: '#334155', padding: '2px 6px', borderRadius: '4px' }}>
                  {letter.fingerAssignment.replace('left', '').replace('right', '')}
                </span>
              </td>

              {/* Trend */}
              <td style={{ padding: '8px 6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{getTrendIcon(letter.trend)}</span>
                  <span style={{
                    fontSize: '10px', 
                    padding: '2px 6px', 
                    borderRadius: '12px', 
                    fontWeight: '500',
                    ...getTrendColorStyle(letter.trend)
                  }}>
                    {letter.trend}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Chart View Component
const ChartView: React.FC<{ data: LetterPerformanceData[] }> = ({ data }) => {
  const [chartType, setChartType] = useState<'accuracy' | 'speed' | 'scatter'>('accuracy');

  const chartData = data.map(letter => ({
    letter: letter.letter,
    accuracy: letter.accuracy,
    speed: letter.avgSpeed,
    practiceCount: letter.practiceCount,
    trend: letter.trend === 'improving' ? 1 : letter.trend === 'declining' ? -1 : 0
  }));

  const getBarColor = (value: number, type: 'accuracy' | 'speed'): string => {
    if (type === 'accuracy') {
      if (value >= 95) return '#10b981'; // green
      if (value >= 90) return '#3b82f6'; // blue  
      if (value >= 85) return '#f59e0b'; // yellow
      return '#ef4444'; // red
    } else {
      if (value <= 150) return '#10b981'; // green
      if (value <= 250) return '#f59e0b'; // yellow
      return '#ef4444'; // red
    }
  };

  return (
    <div>
      {/* Chart Type Selector */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        <button
          onClick={() => setChartType('accuracy')}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            background: chartType === 'accuracy' ? '#3b82f6' : '#334155',
            color: '#ffffff'
          }}
        >
          📊 Accuracy
        </button>
        <button
          onClick={() => setChartType('speed')}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            background: chartType === 'speed' ? '#3b82f6' : '#334155',
            color: '#ffffff'
          }}
        >
          ⚡ Speed
        </button>
        <button
          onClick={() => setChartType('scatter')}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            background: chartType === 'scatter' ? '#3b82f6' : '#334155',
            color: '#ffffff'
          }}
        >
          🎯 Accuracy vs Speed
        </button>
      </div>

      {/* Chart */}
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'scatter' ? (
            <ScatterChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="accuracy" 
                stroke="#6b7280"
                tick={{ fontSize: 12 }}
                label={{ value: 'Accuracy (%)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                dataKey="speed"
                stroke="#6b7280" 
                tick={{ fontSize: 12 }}
                label={{ value: 'Speed (ms)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value: any, name: string, _props: any) => [
                  name === 'speed' ? `${value}ms` : `${value}%`,
                  name === 'speed' ? 'Avg Speed' : 'Accuracy'
                ]}
                labelFormatter={(_label: any, payload: any) => {
                  const data = payload?.[0]?.payload;
                  return data ? `Letter: ${data.letter}` : '';
                }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Scatter dataKey="speed" fill="#3b82f6">
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getBarColor(entry.accuracy, 'accuracy')} 
                  />
                ))}
              </Scatter>
            </ScatterChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="letter" 
                stroke="#6b7280"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke="#6b7280" 
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value: any) => [
                  chartType === 'accuracy' ? `${value}%` : `${value}ms`,
                  chartType === 'accuracy' ? 'Accuracy' : 'Avg Speed'
                ]}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar 
                dataKey={chartType} 
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getBarColor(entry[chartType], chartType)} 
                  />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Summary Stats Component
const SummaryStats: React.FC<{
  data: LetterPerformanceData[];
  filteredData: LetterPerformanceData[];
}> = ({ data }) => {

  const totalPractice = data.reduce((sum, letter) => sum + letter.practiceCount, 0);
  const avgAccuracy = data.length > 0 
    ? data.reduce((sum, letter) => sum + letter.accuracy, 0) / data.length 
    : 0;
  const avgSpeed = data.length > 0
    ? data.reduce((sum, letter) => sum + letter.avgSpeed, 0) / data.length
    : 0;

  const improvingCount = data.filter(letter => letter.trend === 'improving').length;

  const masteredCount = data.filter(letter => letter.accuracy >= 95 && letter.avgSpeed <= 200).length;

  return (
    <div>
      <h3 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>Performance Summary</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px' }}>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>{data.length}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Letters Tracked</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>{totalPractice.toLocaleString()}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Total Practice</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#16a34a' }}>{avgAccuracy.toFixed(1)}%</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Avg Accuracy</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ca8a04' }}>{avgSpeed.toFixed(0)}ms</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Avg Speed</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#16a34a' }}>{improvingCount}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Improving</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#8b5cf6' }}>{masteredCount}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Mastered</div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const LetterPerformanceSkeletonBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <div>
        <div style={{ height: '16px', background: '#475569', borderRadius: '4px', width: '180px', marginBottom: '8px' }}></div>
        <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '200px' }}></div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '120px' }}></div>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '80px' }}></div>
      </div>
    </div>
    
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
          <div style={{ height: '24px', background: '#475569', borderRadius: '4px', width: '24px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '48px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '36px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '48px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '36px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '48px' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '36px' }}></div>
        </div>
      ))}
    </div>
  </div>
);

// Empty State
const EmptyLetterPerformanceBlock: React.FC<{ period: TimePeriod }> = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔤</div>
      <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
        No Letter Data
      </h3>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>
        No letter statistics found for the selected time period.
      </p>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        Complete some typing practice to see detailed letter analysis! ⌨️
      </div>
    </div>
  </div>
);

export default LetterPerformanceMatrix;